import dayjs from 'dayjs';
import {
  connectSocket,
  callAPI,
  setAuthRequired,
} from '@/store/socket';
import { startCallQueue } from '@/persistedQueue';
import config from '@/config';
import router from '@/router';
import store from '@/store';
import i18n from '@/i18n';
import eventBus from '@/eventBus';
import trackerEngine from '@/trackingEngine';
import { checkWhetherMainWindow, lastTrackedTaskOfOpenWorkspace, openLastProjectOfCurrentWorkspace, readFileAsDataURL } from './utils';
import { sneakyPromise } from './profiling';
import { formatDayoffDateRange } from '@/utils';

let ipcRenderer;
if (window.APP_TRON) {
  ipcRenderer = window.APP_TRON.ipcRenderer;
  ipcRenderer.on('failedInstallUpdate', () => {
    console.log('Error: failed to install update');
    eventBus.$emit('failedInstallUpdate');
  });
  ipcRenderer.on('appUpdateProgress', (_, percent) => eventBus.$emit('appUpdateProgress', percent));
}


window.isMainWindow = checkWhetherMainWindow();

if (window.isMainWindow) {
  connectSocket({
    fromApp: false,
    onConnect: async (isReconnect) => {
      await startCallQueue();
      await Promise.all([
        store.dispatch('user/actionGetWorkspace'),
        store.dispatch('reports/getUserActriveTask'),
      ]);
      store.dispatch('user/actionGetUserWorkspaces');
      store.dispatch('uiTheme/actionLoadThemes');
      store.dispatch('project/reconnectionToServer');
      store.dispatch('user/actionCheckUnhandledMentions');
      const channel = new BroadcastChannel('SW_READY');
      channel.postMessage('');
      channel.close();

      store.commit('system/clearAlert', {
        alertId: 'connection',
      }, { root: true });
      if (isReconnect && router.currentRoute.name === 'project') {
        console.log('👑 👑 👑 connectSocket calls actionGetProject');
        await router.isReady(); // just in case
        await store.dispatch('project/actionGetProject', {
          quiet: true, // don't reload with spinner
          slug: router.currentRoute.params.projectSlug ? router.currentRoute.params.projectSlug : 'last',
        });
      }
      if (!isReconnect) {
        store.dispatch('user/actionGetAppVersions');
      }
    },
    imitationConnect: async () => { // it is for cache
      await store.dispatch('user/actionGetWorkspace');
      await store.dispatch('user/actionGetUserWorkspaces');
      await store.dispatch('uiTheme/actionLoadThemes');
    },
    onDisconnect: async () => {
      store.commit('system/showAlert', {
        // message: 'Connection lost. Please don\'t close tab until it will recover to save work',
        message: i18n.global.t('notifications.offline_mode'),
        alertId: 'connection',
        type: 'warning',
        closable: false,
      }, { root: true });
    },
    onTokenExpired: async () => {
      console.log('Token expired, system status: Offline');
      await store.dispatch('user/actionValidateAndParseToken', { getNode: false });
    },
    onAccessDenied: () => {
      router.push({ path: '/access-denied/' });
    },
  });
} else {
  console.log('Not main window, not connecting socket');
}

let lastTrackedTaskResumed = false;

// the languages of the site's pages (and of the API's texts)
const SITE_LANGUAGES = ['en', 'ru', 'uk'];

function autoResumLastTrackedTask(user) {
  // only in electron because permission requests require user guesture
  if (!window.APP_TRON) {
    return;
  }
  const taskToStartTrack = localStorage.getItem('LAST_TASK');
  const ttst = JSON.parse(taskToStartTrack);
  // a task another account tracked in this app is neither resumed nor offered to resume
  if (ttst && !ttst.taskTrackedByUsers?.some(ttbu => ttbu.userId === user.id)) {
    localStorage.removeItem('LAST_TASK');
    localStorage.removeItem('RESUME_LAST_TASK');
    return;
  }
  // autoresume the tracking the window closing cut, once per app start
  if (!lastTrackedTaskResumed && localStorage.getItem('RESUME_LAST_TASK')) {
    lastTrackedTaskResumed = true;
    localStorage.removeItem('RESUME_LAST_TASK');
    const taskToResume = lastTrackedTaskOfOpenWorkspace();
    console.log('✅ taskToStartTrack', taskToResume);
    if (taskToResume) {
      try {
        store.dispatch('task/startTracking', {task: taskToResume, manual: false, retry: false});
      } catch (e) {
        console.error('Error starting task from localStorage', e);
        localStorage.removeItem('LAST_TASK');
      }
    }
  }
}

function showTronTaskNotifyWindow(data) {
  console.log('🔔 showTronTaskNotifyWindow', data);
  if (window.APP_TRON) {
    ipcRenderer.send(
      'notifyUserAboutTask',
      JSON.parse(JSON.stringify({
        ...data,
        srcLoc: document.location.href,
      })),
    );
  }
}


export default {
  namespaced: true,
  state: {
    user: null,
    myTasksByGroup: null,
    unhandledNotifics: false,
    unhandledMentions: false,
    workspace: null,
    workspaces: [],
    routerReady: false,
    lastAppSessionId: null,
    sharedData: null,
    deferredPrompt: null,
    appVersions: [],
    notifications: [],
    lastScreenshots: [],
    tasks: null,
    likedTasks: [
      {
        id: '123', projectStage: true, image: { h: '98' }, tags: true, rows: ['threequarters'],
      },
      { id: '789', projectStage: true, rows: ['fullwidth', 'halfwidth'] },
    ],
    recentTasks: [],
    mentionedTasks: [
      {
        id: '441', projectStage: true, tags: true, rows: ['fullwidth', 'fullwidth', 'halfwidth'],
      },
      {
        id: '411', projectStage: true, image: { h: '130' }, rows: ['fullwidth'],
      },
    ],
    appOpenState: false,
    updateAvailable: '',
  },
  getters: {
    getUser(state) {
      return (userId) => {
        if (!userId) {
          console.error(`Can't get user without userId ${JSON.stringify(userId)}`);
          // debugger; // eslint-disable-line
          return {};
        }
        if (state.workspace) {
          return state.workspace.users.find(usr => usr.id === userId);
        }
        return {};
      };
    },
    getUserByEmail(state) {
      return (email) => {
        if (!email) {
          console.error(`Can't get user without email ${JSON.stringify(email)}`);
          // debugger; // eslint-disable-line
          return {};
        }
        if (state.workspace) {
          return state.workspace.users.find(usr => usr.email === email);
        }
        return {};
      }
    },
    accessTrackingBrowser() {
      return true;
    },
    isWorkspaceAdmin(state) {
      if (!state.user) {
        return false;
      }
      const user = state.workspace.users.find(x => x.id === state.user.id);
      return user.userWorkspace.role === 'ADMIN';
    },
  },
  mutations: {
    setAppOpenState(state, newValue) {
      state.appOpenState = newValue;
    },
    clearNotificationsList(state) {
      state.notifications = [];
    },
    routerReady(state) {
      state.routerReady = true;
    },
    setSharedData(state, sharedData) {
      state.sharedData = sharedData;
    },
    setUser(state, user) {
      if (user === null) {
        document.cookie = "X-Authorization=''; path=/";
        state.user = user;
      } else {
        state.user = user;
      }

      if (user) {
        autoResumLastTrackedTask(user); // we do it here because it requires user to be set
      }
      

    },
    mutationLastScreenshots(state, data) {
      const newData = data.map(x => ({ ...x, user: { id: x.userId } }));
      state.lastScreenshots = newData;
    },
    mutationAddNewScreenshot(state, data) {
      const screenshot = { ...data, user: { id: data.userId } };
      state.lastScreenshots.unshift(screenshot);
      state.lastScreenshots.splice(3);
    },
    mutationRemoveScreenshot(state, data) {
      const indx = state.lastScreenshots.findIndex(x => x.id === data.id);
      if (indx !== undefined && indx !== -1) {
        state.lastScreenshots.splice(indx, 1);
      }
    },
    mutationUpdateAvailable(state, data) {
      // priority:
      // 1. critical
      // 2. minor
      // 3. patch
      const listPriority = ['critical', 'minor', 'patch'];

      const currentPriority = listPriority.indexOf(state.updateAvailable);
      const newPriority = listPriority.indexOf(data);

      if (currentPriority === -1 || newPriority < currentPriority) {
        state.updateAvailable = data;
      }
    },
    mutationMyTasks(state, data) {
      state.myTasksByGroup = data.tasksGroups;
    },
    mutationLikesAndMentions(state, data) {
      state.likedTasks = data.likedTasks;
      state.mentionedTasks = data.mentionedTasks;
    },
    excludeTaskFromMyTasks(state, data) {
      if (router.currentRoute.value.name !== 'myTasks') {
        console.log('Received excludeTaskFromMyTasks but we are not in myTasks page, ignore');
        return;
      }

      const { id } = data;
      state.myTasksByGroup.forEach((group) => {
        const idx = group.tasks.findIndex(task => task.id === id);
        if (idx !== -1) {
          group.tasks.splice(idx, 1);
        }
      });
    },
    updateTaskInMyTasks(state, updTask) {
      if (router.currentRoute.value.name !== 'myTasks') {
        console.log('Received updateTaskInMyTasks but we are not in myTasks page, ignore');
        return;
      }

      console.log('⬆️✔️ updateTaskInMyTasks', updTask);
      if (!state.myTasksByGroup) {
        return;
      }
      // first try to find task in myTasksByGroup
      let found = false;
      for (let group of state.myTasksByGroup) {
        const idx = group.tasks.findIndex(task => task.id === updTask.id);
        if (idx !== -1) {
          group.tasks[idx] = { ...group.tasks[idx], ...updTask };
          found = true;
          break;
        }
      }
      if (!found) {
        // not found we need to add it at right place
        // first figure out taskGroupId
        let taskGroupId;
        if (!updTask.priority && updTask.priority !== 0) {
          console.error('Received publish to my tasks, but we need to know priority to insert into myTasks');
          return;
        }
        if (updTask.priority > 0) {
          taskGroupId = 'urgency';
        } else if (updTask.priority < 0) {
          taskGroupId = 'low-prio';
        } else {
          if (!updTask.project) {
            console.error('Received publish to my tasks, but we need to know project to insert into myTasks');
            return;
          }
          taskGroupId = `project-${updTask.project.id}`;
        }
        // now find group by taskGroupId
        const groupIdx = state.myTasksByGroup.findIndex(group => group.groupId === taskGroupId);
        if (groupIdx === -1) {
          // group found, we can add task to it
          // TODO - here we put new group to bottom though it might be higher
          // This case is rare (received assign to new task on project when user had no tasks in project DURING MYTASKS OPENED), on reopen api will be recalled and group will be prioritized correctly!
          state.myTasksByGroup.push({
            groupId: taskGroupId,
            groupName: updTask.project.name,
            tasks: [updTask],
          });
        } else {
          const existingTasks = state.myTasksByGroup[groupIdx].tasks;

          // insert at correct order (highest order is located at beginning of array)
          if (!updTask.order && updTask.order !== 0) {
            console.error('Received publish to my tasks, but we need to know order to insert into myTasks');
            return;
          }
          const insertIndex = existingTasks.findIndex(task => task.order < updTask.order);
          if (insertIndex === -1) {
            // insert at the end
            state.myTasksByGroup[groupIdx].tasks.push(updTask);
          } else {
            // insert at found index
            state.myTasksByGroup[groupIdx].tasks.splice(insertIndex, 0, updTask);
          }
        }
      }

    },
    mutationMyRecentTasks(state, data) {
      state.recentTasks = data.recentTasks;
    },
    mutationUserRole(state, data) {
      const userIdx = state.workspace.users
        && state.workspace.users.findIndex(x => x.id === data.userId);

      if (userIdx !== -1) {
        state.workspace.users[userIdx].userWorkspace = { 
          ...state.workspace.users[userIdx], role: data.role };
      }
    },
    setDeferredPrompt(state, deferredPrompt) {
      state.deferredPrompt = deferredPrompt;
    },
    async setWorkspaceLocale(state, locale) {
      state.workspace.locale = locale;
      i18n.global.locale = locale;
      dayjs.locale(locale);
      if (window.APP_TRON) {
        ipcRenderer.send('setLocaleApplication', {
          __tracking_stopped: i18n.global.t('system.__tracking_stopped'),
          __tracking_stopped_shutdown: i18n.global.t('system.__tracking_stopped_shutdown'),
        });
      }
    },
    setAppVersions(state, appVersions) {
      state.appVersions = appVersions;
    },
    setUnhandledNotifics(state, value) {
      state.unhandledNotifics = value;
    },
    setUnhandledMentions(state, value) {
      state.unhandledMentions = value;
    },
    addUnhandledMentions(state) {
      state.unhandledMentions += 1;
    },
    removeUnhandledMentions(state) {
      state.unhandledMentions -= 1;
    },
    addNotification(state) {
      state.unhandledNotifics += 1;
    },
    removeNotification(state, notification) {
      const idx = state.notifications.findIndex(n => n.id === notification);
      if (idx !== -1) {
        state.notifications[idx].deleted = 1;
      }
    },
    notificationsClearedForTask(state, { taskId }) {
      let clearedCount = 0;
      state.notifications = state.notifications.map((n) => {
        if (n.tagId === taskId && n.deleted !== 1) {
          clearedCount += 1;
          return { ...n, deleted: 1 };
        }
        return n;
      });
      state.unhandledNotifics = Math.max(0, state.unhandledNotifics - clearedCount);
    },
    notificationsClearedByType(state, { type }) {
      let clearedCount = 0;
      state.notifications = state.notifications.map((n) => {
        if (n.type === type && n.deleted !== 1) {
          clearedCount += 1;
          return { ...n, deleted: 1 };
        }
        return n;
      });
      state.unhandledNotifics = Math.max(0, state.unhandledNotifics - clearedCount);
    },
    notificationsClearedByIds(state, { ids }) {
      let clearedCount = 0;
      state.notifications = state.notifications.map((n) => {
        if (ids.includes(n.id) && n.deleted !== 1) {
          clearedCount += 1;
          return { ...n, deleted: 1 };
        }
        return n;
      });
      state.unhandledNotifics = Math.max(0, state.unhandledNotifics - clearedCount);
    },
    removeMemberFromWorkspace(state, userId) {
      const users = [...state.workspace.users];
      const indexUser = users.findIndex(x => x.id === userId);
      if (indexUser !== -1) {
        users.splice(indexUser, 1);
        state.workspace.users = users;
      }
    },
    updateNotification(state, notification) {
      const idx = state.notifications.findIndex(n => n.id === notification.notificationId);
      if (idx !== -1) {
        state.notifications[idx].state = notification.state;
      }
    },
    restoreNotification(state, notification) {
      const idx = state.notifications.findIndex(n => n.id === notification.id);
      if (idx !== -1) {
        state.notifications[idx] = notification;
      }
    },
    setCurrentWorkspace(state, workspace) {
      state.workspace = workspace;
    },
    workspaceMemberUpdate(state, user) {
      const exIndex = +state.workspace.users.findIndex(t => t.id === user.id);
      const u = { ...user };
      if (u.newId) {
        u.id = u.newId;
        delete u.newId;
      }

      if (exIndex !== -1) {
        state.workspace.users[exIndex] = { ...state.workspace.users[exIndex], ...u };
        state.workspace.users[exIndex].userWorkspace = { ...state.workspace.users[exIndex].userWorkspace, ...u.userWorkspace };
      } else {
        state.workspace.users.push(u);
      }
    },
    workspaceUpdate(state, workspace) {
      state.workspace = { ...state.workspace, ...workspace };
      const listed = state.workspaces.find(ws => ws.id === workspace.id);
      if (listed) Object.assign(listed, workspace);
    },
    appStarted(state, data) {
      state.lastAppSessionId = data.appSessionId;
    },
    forceStopTracking(_, data) {
      if (data.socketId) {
        console.log('window.tracklifyWebSocketState?.ws?.id', window.tracklifyWebSocketState?.ws?.id);
        if (window.tracklifyWebSocketState?.ws?.id !== data.socketId) {
          store.dispatch('task/stopTracking', { action: 'force_stop', force: true });
        }
      } else {
        store.dispatch('task/stopTracking', { action: 'force_stop', force: true });
      }
    },
    autoClickerDetected() {
      try {
        (async () => {
          console.log('>>> autoClickerDetected starting screenshot');
          let screenshot;

          if (window.APP_TRON) {
            screenshot = await trackerEngine.electronFullScreenScreenshot();
          } else {
            screenshot = await trackerEngine.fullScreenScreenshot();
          }
          console.log('>>> autoClickerDetected screenshot done');

          await callAPI({
            method: 'uploadDetectedScreenshots',
            params: {
              base64data: screenshot.imageDataUrl,
            },
          });

          store.dispatch('task/stopTracking', { action: 'force_stop', force: true });
          alert('Auto clicker detected, current time slot will be rejected');
        })();
      } catch (e) {
        console.log('>>> error', e);

        store.dispatch('task/stopTracking', { action: 'force_stop', force: true });
        alert('Auto clicker detected, current time slot will be rejected');
      }
    },
    newAppVersionAdded(state, newAppVersion) {
      const versionIndex = state.appVersions.findIndex(
        version => version.platform === newAppVersion.platform,
      );

      if (versionIndex !== -1) {
        const updVersion = { ...state.appVersions[versionIndex] };
        updVersion.major = newAppVersion.major;
        updVersion.minor = newAppVersion.minor;
        updVersion.createdAt = newAppVersion.createdAt;
        state.appVersions[versionIndex] = { ...state.appVersions[versionIndex], ...updVersion };
      }
      store.dispatch('user/checkForUpdates');
    },
    setWorkspaces(state, workspaces) {
      state.workspaces = workspaces;
    },
    addNotifications(state, data) {
      state.notifications = [...state.notifications, ...data.elements];
    },
    
    showTaskNotifyToUser(state, rawData) {
      let data = rawData;
      if (data.type === 'assign') {
        data = {
          ...data,
          commentText: i18n.global.t('notifications.new_assign'),
          iconBrowser: '/favicon-192-orange.png',
          buttons: [
            {
              text: i18n.global.t('search.open'),
              ipcAction: 'openTaskDialogFromNotification',
              ipcData: { task: data.task },
              action: 'hideWindow',
            },
            {
              text: i18n.global.t('system.__hide_button'),
              autoClickAfter: 5,
              action: 'hideWindow',
            },
          ],
        };
      } else if (data.type === 'dayoff') {
        const dateRange = formatDayoffDateRange(data.dates);
        data = {
          ...data,
          taskHid: '',
          taskTitle: i18n.global.t('notifications.dayoff_title'),
          commentText: data.description ? `${dateRange} — ${data.description}` : dateRange,
          buttons: [{ text: i18n.global.t('system.__hide_button'), autoClickAfter: 5, action: 'hideWindow' }],
        };
      }
      if (window.APP_TRON) {
        showTronTaskNotifyWindow(data)
      } else {
        // drop os notification now
        if (Notification.permission === 'granted') {
          let prio = '';
          if (data.taskPriority > 0) {
            prio = `⚠️`;
          }

          let title = `${prio}${data.taskHid}`;
          const taskTitle = data.taskTitle || '';
          if (taskTitle.length > 100) {
            title = `${title}: ${taskTitle.slice(0, 100)}..`;
          } else {
            title = `${title}: ${taskTitle}`;
          }

          let body = `${data.commentUser.username}: `;
          if (data.commentText.length > 100) {
            body += `${data.commentText.slice(0, 100)}..`;
          } else {
            body += data.commentText;
          }
          const notification = new Notification(title, {
            body: body,
            icon: data.iconBrowser,
          });
          notification.onclick = () => {
            window.focus();
            if (data.task) {
              store.dispatch('project/actionGetTaskAndOpen', data.task);
            }
            notification.close();
          };
        } else {
          console.warn('Notification permission is not granted, can not show notification', data);
        }


      }
    },
  },
  actions: {
    actionSetDeferredPrompt({ commit }, deferredPrompt) {
      commit('setDeferredPrompt', deferredPrompt);
    },
    async actionValidateAndParseToken(_, { getNode }) {
      if (window.refreshingTokenInProgress) {
        console.warn('>>> ⛔️⛔️⛔️ validate_and_parse_token already in progress');
        return '';
      }
      window.refreshingTokenInProgress = true;
      let response = '';
      try {
        const controller = new AbortController();
        const { signal } = controller;
        const vaptStopper = setTimeout(controller.abort, 3_000); // to go offline mode faster when there is slow response

        const res = await fetch(`${config.getSSRDomain()}/api/validate_and_parse_token${window.APP_TRON ? '?source=app' : ''}`, {
          method: 'post',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(window.APP_TRON && process.env.VUE_APP_ENV !== 'live' ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
          },
          body: JSON.stringify({ getNode }),
          signal,
        });

        clearTimeout(vaptStopper);
        if (res.status === 401) {
          if (!window.APP_TRON) {
            console.error('Was not able to refresh, forward user to login');
            setTimeout(() => {
              window.location.href = `${config.getSSRDomain()}/login/`;
            }, 1000); // I am trying to postpone redirect because seams like coockie is not cleaned instantly
            // so we get SPA on /login which is awful
          } else {
            console.error('Was not able to refresh, requiring auth');
            setAuthRequired(true);
          }
          window.refreshingTokenInProgress = false;
          return false;
        }
        response = await res.json();
      } catch (e) {
        console.log('>>> ⛔️⛔️⛔️ validate_and_parse_token err', e);
        throw e;
      }
      window.refreshingTokenInProgress = false;
      return response;
    },
    async actionGetNotificationPaged({ commit }, query) {
      const res = await callAPI({
        method: 'getNotificationsPaged',
        params: query,
      });
      commit('addNotifications', res);
      return res;
    },
    async actionGetUserWorkspaces({ state, commit }) {
      if (state.workspaces.length) return [];
      const controller = new AbortController();
      const timerAbort = setTimeout(() => {
        console.log('>>> ⛔️⛔️⛔️ get_workspaces');
        controller.abort();
        commit('setWorkspaces', []);
        return [];
      }, 6000);

      try {
        const url = `${config.getSSRDomain()}/api/get_workspaces`;
        const request = await fetch(url, {
          signal: controller.signal,
          method: 'GET',
          credentials: 'include',
          headers: {
            ...(window.APP_TRON && process.env.VUE_APP_ENV !== 'live' ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
          },
        });
        clearInterval(timerAbort);
        const workspaces = await request.json();
        commit('setWorkspaces', workspaces.workspaces.map(x => ({
          id: x.uuid,
          name: x.name,
          avatarFileName: x.logo,
        })));
        return [];
      } catch (e) {
        console.log('>>> ⛔️⛔️⛔️ Error get workspaces', e);
      }
      commit('setWorkspaces', []);
      return [];
    },
    async actionSetNotificationState({ commit }, notification) {
      commit('updateNotification', notification);
      await callAPI({
        method: 'updateNotification',
        params: notification,
        persisted: true,
      });
    },
    async initConnection({ commit }) {
      await router.isReady();

      // important: never move this line to bottom to fix some race conditions
      // moving to bottom will slow-down rendering of the app. Fix race conditions locally instead - where they happen
      commit('routerReady');

      let user;
      let sessionValid = false;
      try {
        user = await store.dispatch('user/actionValidateAndParseToken', { getNode: false });
        sessionValid = Object.keys(user).length > 0;
        if (sessionValid) {
          localStorage.setItem('TRACKLIFY_USER', JSON.stringify(user));
        }
      } catch (e) {
        console.error('Error during user validation', e);
        user = JSON.parse(localStorage.getItem('TRACKLIFY_USER'));
      }

      commit('setUser', user);
      // the login screen goes away only once the user is set: the dashboard reads it on its first render
      if (sessionValid) {
        setAuthRequired(false);
      }

    },
    // the API makes it the current workspace (cookie); then everything loads again for it
    async actionSwitchWorkspace(_, workspaceId) {
      await fetch(`${config.getSSRDomain()}/api/update_last_ws`, {
        method: 'post',
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        redirect: 'follow',
        referrer: 'no-referrer',
        body: JSON.stringify({ uuid: workspaceId }),
      });
      openLastProjectOfCurrentWorkspace();
    },
    // the API creates it and makes it the current workspace (cookie), as the site's starting page does
    async actionCreateWorkspace({ state }, { wsname, isPersonal }) {
      const res = await fetch(`${config.getSSRDomain()}/api/create_ws`, {
        method: 'post',
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        // the API knows the site's languages only; another one leaves its default
        body: JSON.stringify({
          wsname,
          is_personal: isPersonal,
          ...(SITE_LANGUAGES.includes(state.workspace.locale) ? { LANG: state.workspace.locale } : {}),
        }),
      });
      if (!res.ok) {
        throw new Error(`create_ws failed (${res.status}): ${await res.text()}`);
      }
      openLastProjectOfCurrentWorkspace();
    },
    async actionLogout({ commit, dispatch, rootState }) {
      // tracking ends with the session: stopped (and sent) while the user is still set
      if (rootState.task.activeTrackingTask) {
        await dispatch('task/stopTracking', { trigEvent: 'pause', manual: true }, { root: true });
      }
      // the API answers with a redirect to the site's landing, which the SPA doesn't need
      await fetch(`${config.getSSRDomain()}/api/logout/`, {
        method: 'get',
        credentials: 'include',
        redirect: 'manual',
      });
      if (window.APP_TRON) {
        // the app's window never navigates to the site: it shows its own login screen
        setAuthRequired(true);
        commit('setUser', null);
        return;
      }
      commit('setUser', null);
      window.location.href = `${config.getSSRDomain()}/login/`;
    },
    async actionUpdateRole({ commit }, userData) {
      commit('mutationUserRole', userData);
      const resp = await callAPI({
        method: 'updateRole',
        params: userData,
        persisted: true,
      });
      return resp;
    },
    async actionSsrUpdateUser({ commit }, userData) {
      const res = await fetch(`${config.getSSRDomain()}/api/update_user`, {
        method: 'post',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(window.APP_TRON ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
        },
        body: JSON.stringify(userData),
      });
      const body = await res.json();
      // a refused update is also answered with 200 and `error` (e.g. another account logged in meanwhile)
      if (!res.ok || body.error) {
        throw new Error(`update_user failed (${res.status}): ${JSON.stringify(body)}`);
      }
      commit('setUser', body);
    },
    async actionUpdateDaysOffPrivacy({ commit }, visibleToGuests) {
      const user = await callAPI({
        method: 'updateDaysOffPrivacy',
        params: { visibleToGuests },
        persisted: true,
      });
      commit('workspaceMemberUpdate', user);
    },
    async actionGetAppVersions({ commit }) {
      console.log('⏬ actionGetAppVersions');
      const res = await fetch(`${config.getSSRDomain()}/api/get_app_versions`, {
        method: 'get',
        credentials: 'include',
        headers: {
          ...(window.APP_TRON ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
        },
      }).then(r => r.json());

      commit('setAppVersions', res.versions);
      if (window.APP_TRON) {
        return store.dispatch('user/checkForUpdates');
      }
      return false;
    },
    async actionCountDownload(_, { major, minor, platform }) {
      await fetch(`${config.getSSRDomain()}/api/count_download/${major}/${minor}/${platform}`, {
        method: 'get',
        credentials: 'include',
      }).then(r => r.json());
    },
    async actionChangePassword(_, data) {
      const resp = await callAPI({
        method: 'changeUserPassword',
        params: data,
        persisted: true,
      });
      return resp;
    },
    async actionCheckUnhandledNotifics({ commit }) {
      const ws = await callAPI({
        method: 'getWorkspaceAndSubscribe',
        params: { caller: 'frontend' },
      });
      const unhandledCount = await callAPI({
        method: 'checkUnhandledNotificsAndSubscribe',
        params: {
          workspaceId: ws.id,
        },
      });
      commit('setUnhandledNotifics', unhandledCount);
    },
    async actionClearTaskNotifications(_, { taskId }) {
      await callAPI({
        method: 'clearTaskNotifications',
        params: { taskId },
      });
    },
    async actionClearDayoffNotifications() {
      await callAPI({ method: 'clearDayoffNotifications' });
    },
    async actionMarkAllNotificationsRead() {
      await callAPI({ method: 'markAllNotificationsRead' });
    },
    async actionClearProjectInvitationNotifications(_, { projectId }) {
      await callAPI({
        method: 'clearProjectInvitationNotifications',
        params: { projectId },
      });
    },
    async actionCheckUnhandledMentions({ commit }) {
      const unhandledCount = await callAPI({
        method: 'checkUnhandledMentionsAndSubscribe',
      });
      commit('setUnhandledMentions', unhandledCount);
    },
    actionUpdateLocale({ commit }, locale) {
      callAPI({
        method: 'updateWorkspaceLocale',
        params: { locale },
        persisted: true,
      });
      commit('setWorkspaceLocale', locale);
    },
    async actionGetWorkspace({ commit }) {
      const ws = await callAPI({
        method: 'getWorkspaceAndSubscribe',
        params: { caller: 'frontend' },
      });
      if (ws.error) {
        throw new Error(`Failed to load workspace: ${ws.error}`);
      }
      commit('setCurrentWorkspace', ws);
      const lang = ws.locale;

      document.querySelector('body').setAttribute('data-locale', lang);
      document.querySelector('html').setAttribute('lang', lang);
      commit('setWorkspaceLocale', lang);
    },
    async actionUpdateWorkspace(_, workspaceData) {
      return callAPI({
        method: 'getWorkspaceAndUpdate',
        params: workspaceData,
        persisted: true,
      });
    },
    async actionDeleteUserFromWorkspace({ commit }, data) {
      commit('removeMemberFromWorkspace', data.userId);
      await callAPI({
        method: 'deleteUserFromWorkspace',
        params: data,
        persisted: true,
      });
    },
    async actionDeleteUserFromTracklify(_, data) {
      await fetch(`${config.getSSRDomain()}/api/delete_user`, {
        method: 'POST',
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          ...(window.APP_TRON && process.env.VUE_APP_ENV !== 'live' ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
        },
        redirect: 'follow',
        referrer: 'no-referrer',
        body: JSON.stringify({ userId: data.userId }),
      });
    },
    async actionSetSharedData(_, sharedData) {
      console.log('~![STORE] actionSetSharedData', sharedData);
      const file = sharedData?.images && sharedData.images[0];
      if (!file) {
        return null;
      }

      const image = new Image();

      if (
          ! (file.type === 'image/png'
          || file.type === 'image/jpg'
          || file.type === 'image/jpeg')
      ) {
          return null;
      }
      const b64Data = await readFileAsDataURL(file);

      image.src = b64Data;
      await image.decode();

      eventBus.emit('open-create-task_from', {
        image: b64Data,
        width: image.width,
        height: image.height,
        mode: 'newTask',
      });

      return null;
    },
    async generateSignedAppToken() {
      // TODO: replace the fetch
      return fetch(`${config.getSSRDomain()}/api/generate_signed_app_token`, {
        method: 'post',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(window.APP_TRON && process.env.VUE_APP_ENV !== 'live' ? { Authorization: 'Basic YWRtaW46YWRtaW4=' } : {}),
        },
        body: JSON.stringify({}),
      }).then(r => r.json()).then(r => r.result);
    },
    async getLastScreenshots({ commit }) {
      const res = await callAPI({
        method: 'userLastScreenshots',
        params: { limit: 3 },
      });
      commit('mutationLastScreenshots', res);
    },
    async actionGetAllMyTasks({ commit }) {
      const result = await callAPI({
        method: 'getAllMyTasksAndSubscribeNext',
        async: true,
        params: {},
      });
      commit('mutationMyTasks', result);
    },
    async actionGetLikesAndMentions({ commit }) {
      const result = await callAPI({
        method: 'getAllMyLikesAndMentions',
        params: {},
      });
      commit('mutationLikesAndMentions', result);
    },
    async actionGetMyRecentTasks({ commit }) {
      const result = await callAPI({
        method: 'getMyRecentTasksAndSubscribe',
        params: {},
      });
      commit('mutationMyRecentTasks', result);
    },
    async actionUpdateApplication({ state }) {
      await sneakyPromise(async (resolve) => {
        setTimeout(resolve, 2000);
        console.log('stopTracking triggered by actionUpdateApplication');
        await store.dispatch('task/stopTracking', { trigEvent: 'pause', manual: true, causedBy: 'appUpdate' });
        resolve();
      });

      if (window.APP_TRON) {
        const platform = {
          linux: 'linux',
          win32: 'win',
          darwin: 'mac',
        }[window.devicePlatform];


        const appVersion = state.appVersions.find(v => v.platform === platform);
        const [major, minor] = window.APP_TRON.appVersion.split('.'); // eslint-disable-line


        if (state.updateAvailable === 'patch') {
          ipcRenderer.send('update-application');
          return;
        }

       
        ipcRenderer.send('newVersionDownloadV2', JSON.parse(JSON.stringify({ appVersion, s3base: window.s3base })));
        
        return;
      }
      window.location.reload();
    },
    ...(window.APP_TRON && {
      async setSignedToken(_, signedToken) {
        const res = await fetch(`${config.getSSRDomain()}/api/issue_tokens_from_signed_token${window.APP_TRON ? '?source=app' : ''}`, {
          method: 'post',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(process.env.VUE_APP_ENV === 'dev' ? { Authorization: `Basic ${Buffer.from('admin:admin').toString('base64')}` } : {}),
          },
          body: JSON.stringify({ signedToken }),
        }).then(r => r.json());
        if (res.success) {
          await store.dispatch('user/initConnection', {});
        } else {
          // store.commit('system/setOffline', false, { root: true });
          // store.commit('tasks/setMyTasks', [], { root: true });
        }
      },
      setWorkspaceAlias(_, workspaceAlias) {
        localStorage.setItem('TRACKLIFY_WORKSPACE', workspaceAlias);
      },
      setNode(_, node) {
        localStorage.setItem('TRACKLIFY_NODE', node);
      },
      async registerApp(_, { appSessionId }) {
        await callAPI({
          method: 'appStarted',
          params: { appSessionId },
          async: true,
        });
      },
      async checkForUpdates({ state, commit }) {
        const platform = {
          linux: 'linux',
          win32: 'win',
          darwin: 'mac',
        }[window.devicePlatform];
        const appVersion = state.appVersions.find(v => v.platform === platform);
        const [major, minor] = window.APP_TRON.appVersion.split('.'); // eslint-disable-line
        console.log('current version =>', major, minor);
        if (appVersion && (appVersion.major > major || appVersion.minor > minor)) {
          console.log('🆕 new version', appVersion.major > major, appVersion.minor > minor);
          commit('mutationUpdateAvailable', 'minor');
          return true;
        }
        return false;
      },
    }),
  },
};
